import React from 'react'
import { 
  Box, 
  CircularProgress, 
  Skeleton,
  Card,
  CardContent,
  Typography,
  Stack
} from '@mui/material'
import { poolwerxColors } from '../../../theme/colors'

// ============================================================================
// Types
// ============================================================================

export type LoadingVariant = 'spinner' | 'skeleton' | 'table' | 'card' | 'custom'

export interface LoadingStateProps {
  /** Loading variant to display */
  variant?: LoadingVariant
  /** Message to show with loading */
  message?: string
  /** Size of the loading indicator */
  size?: 'small' | 'medium' | 'large'
  /** Number of skeleton rows/items */
  rows?: number
  /** Custom loading component */
  children?: React.ReactNode
  /** Additional styling */
  sx?: object
}

// ============================================================================
// Skeleton Components
// ============================================================================

const TableSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <Box sx={{ p: 2 }}>
    {/* Table Header Skeleton */}
    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
      <Skeleton variant="text" width="20%" height={40} />
      <Skeleton variant="text" width="25%" height={40} />
      <Skeleton variant="text" width="15%" height={40} />
      <Skeleton variant="text" width="20%" height={40} />
      <Skeleton variant="text" width="20%" height={40} />
    </Stack>
    
    {/* Table Rows Skeleton */}
    {Array.from({ length: rows }).map((_, index) => (
      <Stack key={index} direction="row" spacing={2} sx={{ mb: 1.5 }}>
        <Skeleton variant="text" width="20%" height={30} />
        <Skeleton variant="text" width="25%" height={30} />
        <Skeleton variant="text" width="15%" height={30} />
        <Skeleton variant="text" width="20%" height={30} />
        <Skeleton variant="text" width="20%" height={30} />
      </Stack>
    ))}
  </Box>
)

const CardSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <Stack spacing={2}>
    {Array.from({ length: rows }).map((_, index) => (
      <Card key={index} variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="text" width="30%" height={24} />
            <Stack direction="row" spacing={2}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
            </Stack>
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="40%" height={16} />
          </Stack>
        </CardContent>
      </Card>
    ))}
  </Stack>
)

const GenericSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <Stack spacing={2}>
    {Array.from({ length: rows }).map((_, index) => (
      <Box key={index}>
        <Skeleton variant="text" width="40%" height={24} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="80%" height={20} />
      </Box>
    ))}
  </Stack>
)

// ============================================================================
// Main Component
// ============================================================================

const LoadingState: React.FC<LoadingStateProps> = ({
  variant = 'spinner',
  message = 'Loading...',
  size = 'medium',
  rows = 3,
  children,
  sx = {}
}) => {
  // Size mappings
  const spinnerSizes = {
    small: 24,
    medium: 40,
    large: 56
  }
  
  const paddingSizes = {
    small: 2,
    medium: 4,
    large: 6
  }
  
  // Custom loading component
  if (variant === 'custom' && children) {
    return <Box sx={sx}>{children}</Box>
  }
  
  // Spinner loading
  if (variant === 'spinner') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: paddingSizes[size],
          ...sx
        }}
      >
        <CircularProgress 
          size={spinnerSizes[size]} 
          sx={{ 
            color: poolwerxColors.teal,
            mb: message ? 2 : 0 
          }} 
        />
        {message && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            textAlign="center"
          >
            {message}
          </Typography>
        )}
      </Box>
    )
  }
  
  // Skeleton loading variants
  const skeletonContent = (() => {
    switch (variant) {
      case 'table':
        return <TableSkeleton rows={rows} />
      case 'card':
        return <CardSkeleton rows={rows} />
      case 'skeleton':
      default:
        return <GenericSkeleton rows={rows} />
    }
  })()
  
  return (
    <Box sx={{ ...sx }}>
      {skeletonContent}
    </Box>
  )
}

export default LoadingState

// ============================================================================
// Specialized Loading Components
// ============================================================================

/**
 * 🔄 Table loading with skeleton rows
 */
export const TableLoadingState: React.FC<{ rows?: number; message?: string }> = ({ 
  rows = 5, 
  message 
}) => (
  <LoadingState variant="table" rows={rows} message={message} />
)

/**
 * 🔄 Card list loading with skeleton cards
 */
export const CardLoadingState: React.FC<{ rows?: number; message?: string }> = ({ 
  rows = 3, 
  message 
}) => (
  <LoadingState variant="card" rows={rows} message={message} />
)

/**
 * 🔄 Fullscreen loading overlay
 */
export const FullscreenLoadingState: React.FC<{ message?: string }> = ({ 
  message = 'Loading...' 
}) => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(2px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <LoadingState variant="spinner" size="large" message={message} />
  </Box>
)

/**
 * 🔄 Inline loading for buttons and small spaces
 */
export const InlineLoadingState: React.FC<{ message?: string }> = ({ 
  message 
}) => (
  <LoadingState 
    variant="spinner" 
    size="small" 
    message={message}
    sx={{ py: 1 }}
  />
)